import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ContentCard } from "components/ContentCard";
import AiccBreadCrumb from "components/common/AiccBreadCrumb";
import CenterLoading from "components/common/CenterLoading";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import RuleEngineActions from "../../components/RuleEngineActions";
import geographicalAPi from "services/apis/geographicalAPi";
import {
  CRUD_GEOGRAPHICAL_TYPES,
  formatDateToMyFormat,
  getPathPrefix,
  nameCapitalize,
} from "utils/coreUtils";
import ToastUtils from "utils/ToastUtils";
import "../../styles/styles.scss";

const GeographicalControl = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();

  const streamObj = useSelector((state) => state?.datastream?.selected);
  const [geographicalData, setGeographicalData] = useState([]);
  const [resetPaginationToggle] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "14px",
        padding: "0.75rem",
        verticalAlign: "middle",
        color: "#004e5c",
        fontWeight: 800,
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        padding: "0.75rem",
        verticalAlign: "middle",
      },
    },
  };

  useEffect(() => {
    getAllGeographicalAreaControl();
  }, []);

  async function getAllGeographicalAreaControl() {
    setIsLoading(true);
    try {
      const response =
        await geographicalAPi.getAllGeographicalAreaControlByDSConfigId({
          dsConfigId: streamObj?.dataStreamConfigId,
        });
      setGeographicalData(response?.data);
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  }

  const columns = useMemo(
    () => [
      {
        name: t("geographical.columns.version"),
        selector: (row) => nameCapitalize(row.geographicalAreaName),
        sortable: true,
        minWidth: "200px",
        className: "",
      },
      {
        name: t("geographical.columns.createdBy"),
        selector: (row) =>
          row.createdBy === null ? "N/A" : nameCapitalize(row.createdBy),
        sortable: true,
        minWidth: "125px",
      },
      {
        name: t("geographical.columns.createdDate"),
        selector: (row) => formatDateToMyFormat(row.createdAt, "DD/MM/YYYY"),
        sortable: true,
        minWidth: "150px",
      },
      {
        name: t("geographical.columns.updatedBy"),
        selector: (row) =>
          row.updatedBy === null
            ? nameCapitalize(row.createdBy)
            : nameCapitalize(row.updatedBy),
        sortable: true,
        minWidth: "130px",
      },
      {
        name: t("geographical.columns.updatedDate"),
        selector: (row) =>
          row.updatedBy === null
            ? formatDateToMyFormat(row.createdAt, "DD/MM/YYYY")
            : formatDateToMyFormat(row.updatedAt, "DD/MM/YYYY"),
        sortable: true,
        minWidth: "150px",
      },
      {
        name: t("geographical.columns.status"),
        cell: (row) => (
          <div className="switch mt-1">
            <input
              type="checkbox"
              id="status"
              name="status"
              checked={row.isActive}
              onChange={(e) => {
                subStatusHandler(row, e.target.checked);
                selectedRow.row = row;
                selectedRow.checked = e.target.checked;
                setSelectedRow({ ...selectedRow });
              }}
            />
          </div>
        ),
        minWidth: "50px",
      },
      {
        name: t("geographical.columns.actions"),
        center: true,
        minWidth: "170px",
        cell: (row) => (
          <RuleEngineActions
            editPath={`selected-datastream/geographical-control/${row.geographicalAreaControlId}?t=${CRUD_GEOGRAPHICAL_TYPES.EDIT}`}
            viewPath={`selected-datastream/geographical-control/${row.geographicalAreaControlId}?t=${CRUD_GEOGRAPHICAL_TYPES.VIEW}`}
            deleteMessage={
              <>
                {t("geographical.delete")} <b>{row?.geographicalAreaName}</b>{" "}
                {t("geographical.columns.delete")}
              </>
            }
            deleteId={row.geographicalAreaControlId}
            deleteHandler={deleteHandler}
          />
        ),
      },
    ],
    []
  );

  const Checkbox = React.forwardRef(({ onClick, ...rest }, ref) => {
    return (
      <>
        <div className="form-check form-check-sm form-check-custom">
          <input
            type="checkbox"
            className="form-check-input"
            ref={ref}
            onClick={onClick}
            {...rest}
          />
        </div>
      </>
    );
  });

  Checkbox.displayName = "Checkbox";

  const deleteHandler = async (id) => {
    try {
      const response = await geographicalAPi.deleteGeographicalAreaControlById({
        geographicalAreaControlId: id,
      });
      if (response.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
      } else {
        toast.success(t(`statusCodes.${response?.data?.code}`));
      }
      getAllGeographicalAreaControl();
    } catch (error) {
      /* empty */
    }
  };

  const subStatusHandler = async (row, isChecked) => {
    const createRequest = {
      geographicalAreaControlId: row.geographicalAreaControlId,
      isActive: isChecked,
      isDeleted: false,
    };

    try {
      const response =
        await geographicalAPi.saveOrUpdateGeographicalAreaControl(
          createRequest
        );
      if (response.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
      } else {
        toast.success(t(`statusCodes.${response?.data?.code}`));
        getAllGeographicalAreaControl();
      }
    } catch (error) {
      //  if (error.response.data.isDSConfigVersionActiveAlready) {
      //    setAlreadyActive(error.response.data.isDSConfigVersionActiveAlready);
      //  }
    }
  };

  return (
    <>
      <AiccBreadCrumb
        homeLink={getPathPrefix("dashboard")}
        title={streamObj?.customDataStreamName}
        items={t("geographical.breadCrumb.items", {
          returnObjects: true,
        })}
      />

      <ContentCard
        headerImgSrc="/assets/images/globe.svg"
        title={t("geographical.geographicalControl")}
        headerContent={
          <div className="d-flex justify-content-between">
            <PrimaryButton
              title={t("geographical.addGeographicalControl")}
              icon="fa-regular fa-square-plus"
              onClick={() => {
                navigate(
                  getPathPrefix() +
                    "selected-datastream/geographical-control/create/"
                );
              }}
            />
          </div>
        }
        bodyContent={
          <div className="table-responsive">
            <DataTable
              responsive
              persistTableHead
              keyField={"id"}
              progressPending={isLoading}
              progressComponent={<CenterLoading minHeight={"150px"} />}
              columns={columns}
              data={geographicalData}
              paginationResetDefaultPage={resetPaginationToggle}
              pagination
              striped
              customStyles={customStyles}
            />
          </div>
        }
      />
    </>
  );
};

export default GeographicalControl;
